/**
 * Graph construction with guaranteed Hamiltonian path
 * 
 * Based on Sara (n.d.) and Mayer & Wünch (2017) on Hamiltonian path theory
 * and NP-completeness.
 * 
 * A Hamiltonian path is a path in a graph that visits each vertex exactly once.
 * Finding such a path is an NP-complete problem - as the number of vertices grows,
 * the time to find a solution grows exponentially.
 * 
 * Educational Purpose:
 * - Demonstrates NP-complete problems in an interactive way
 * - Shows how problem complexity scales with size
 * - Illustrates graph theory concepts (vertices, edges, paths)
 * 
 * Implementation Strategy:
 * 1. Create a guaranteed Hamiltonian path (linear: 0→1→2→...→n-1)
 * 2. Add random edges to increase complexity
 * 3. Enforce one-way traversal (visited rooms lock their doors)
 */

import { CFG } from './CFG';
import { Room, GraphConfig } from './types';

export class Graph {
  private numRooms: number;
  private rooms: Map<number, Room>;
  private edges: Map<number, number[]>;
  private solutionPath: number[];
  private cfg: CFG;
  private randomEdgeProbability: number;

  /**
   * Initialize the graph with a specified number of rooms
   * 
   * @param config - Configuration object or number of rooms
   */
  constructor(config: GraphConfig | number) {
    if (typeof config === 'number') {
      this.numRooms = config;
      this.randomEdgeProbability = 0.3; // Default 30% chance
    } else {
      this.numRooms = config.numRooms;
      this.randomEdgeProbability = config.randomEdgeProbability ?? 0.3;
    }

    this.rooms = new Map();
    this.edges = new Map();
    this.solutionPath = [];
    this.cfg = new CFG();

    // Build the graph structure
    this.generateRooms();
    this.generateSolutionPath();
    this.addRandomEdges();
  }

  /**
   * Generate all rooms with unique CFG-generated descriptions
   * Each room gets a procedurally generated description that makes
   * every playthrough feel unique
   */
  private generateRooms(): void {
    for (let i = 0; i < this.numRooms; i++) {
      this.rooms.set(i, {
        id: i,
        description: this.cfg.generate(),
        visited: false,
      });
      // Initialize empty adjacency list for this room
      this.edges.set(i, []);
    }
  }

  /**
   * Generate the guaranteed Hamiltonian path
   *
   * Creates a RANDOMIZED path through all rooms to ensure the puzzle
   * is not trivially easy. The path is shuffled so it's not simply 0→1→2→...
   * This ensures there is always at least one solution, but it's not obvious.
   *
   * The path is bidirectional (you can go forward or backward) until
   * a room is visited, at which point it becomes locked.
   */
  private generateSolutionPath(): void {
    // Create array of all room IDs
    const roomIds: number[] = Array.from({ length: this.numRooms }, (_, i) => i);
    
    // Fisher-Yates shuffle to randomize the path
    // Always keep room 0 as the starting point
    for (let i = roomIds.length - 1; i > 1; i--) {
      const j = Math.floor(Math.random() * (i - 1)) + 1; // Skip index 0
      const temp = roomIds[i] as number;
      roomIds[i] = roomIds[j] as number;
      roomIds[j] = temp;
    }
    
    // Store the randomized solution path
    this.solutionPath = roomIds;

    // Connect consecutive rooms in the shuffled path (bidirectional edges)
    for (let i = 0; i < this.solutionPath.length - 1; i++) {
      const currentRoom = this.solutionPath[i] as number;
      const nextRoom = this.solutionPath[i + 1] as number;
      
      this.edges.get(currentRoom)!.push(nextRoom);
      this.edges.get(nextRoom)!.push(currentRoom);
    }
  }

  /**
   * Add random edges to increase puzzle complexity
   * 
   * This creates additional connections between non-adjacent rooms,
   * making the graph more complex and the puzzle harder. These extra
   * edges can lead to dead ends if chosen incorrectly.
   * 
   * The probability of adding an edge can be configured to control
   * difficulty. Higher probability = more connections = harder puzzle.
   */
  private addRandomEdges(): void {
    for (let i = 0; i < this.numRooms; i++) {
      // Only consider rooms that are at least 2 steps apart
      for (let j = i + 2; j < this.numRooms; j++) {
        if (Math.random() < this.randomEdgeProbability) {
          const edgesI = this.edges.get(i)!;
          const edgesJ = this.edges.get(j)!;

          // Only add edge if it doesn't already exist
          if (!edgesI.includes(j)) {
            edgesI.push(j);
            edgesJ.push(i);
          }
        }
      }
    }
  }

  /**
   * Get all rooms in the graph
   * @returns Map of room IDs to Room objects
   */
  getRooms(): Map<number, Room> {
    return this.rooms;
  }

  /**
   * Get a specific room by ID
   * @param id - Room ID
   * @returns Room object or undefined if not found
   */
  getRoom(id: number): Room | undefined {
    return this.rooms.get(id);
  }

  /**
   * Get the adjacency list (all edges)
   * @returns Map of room IDs to arrays of connected room IDs
   */
  getEdges(): Map<number, number[]> {
    return this.edges;
  }

  /**
   * Get edges for a specific room
   * @param roomId - Room ID
   * @returns Array of connected room IDs
   */
  getEdgesForRoom(roomId: number): number[] {
    return this.edges.get(roomId) ?? [];
  }

  /**
   * Get the guaranteed solution path
   * This is the Hamiltonian path that always exists
   * 
   * @returns Array of room IDs in solution order
   */
  getSolutionPath(): number[] {
    return [...this.solutionPath];
  }

  /**
   * Get the total number of rooms
   * @returns Number of rooms in the graph
   */
  getNumRooms(): number {
    return this.numRooms;
  }

  /**
   * Check if two rooms are connected
   * @param roomA - First room ID
   * @param roomB - Second room ID
   * @returns True if rooms are connected
   */
  areConnected(roomA: number, roomB: number): boolean {
    const edges = this.edges.get(roomA);
    return edges ? edges.includes(roomB) : false;
  }

  /**
   * Get the degree (number of connections) of a room
   * Useful for analyzing graph structure
   * 
   * @param roomId - Room ID
   * @returns Number of edges connected to this room
   */
  getDegree(roomId: number): number {
    return this.edges.get(roomId)?.length ?? 0;
  }

  /**
   * Get graph statistics for educational display
   * @returns Object containing graph metrics
   */
  getStatistics(): {
    numRooms: number;
    numEdges: number;
    avgDegree: number;
    maxDegree: number;
    minDegree: number;
  } {
    let totalEdges = 0;
    let maxDegree = 0;
    let minDegree = Infinity;

    for (const edges of this.edges.values()) {
      const degree = edges.length;
      totalEdges += degree;
      maxDegree = Math.max(maxDegree, degree);
      minDegree = Math.min(minDegree, degree);
    }

    // Each edge is counted twice (once for each endpoint)
    totalEdges = totalEdges / 2;

    return {
      numRooms: this.numRooms,
      numEdges: totalEdges,
      avgDegree: (totalEdges * 2) / this.numRooms,
      maxDegree,
      minDegree: minDegree === Infinity ? 0 : minDegree,
    };
  }
}